import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft,
  Edit,
  Eye,
  Lock,
  Book,
  Clock,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import toast from 'react-hot-toast';
import studyBuilderService from '../../services/studyBuilderService';
import permissionsService from '../../services/permissionsService';
import ReviewWorkflowActions from '../AssessmentBuilder/ReviewWorkflowActions';
import StudyDraftDetailModal from './StudyDraftDetailModal';
import { APPROVE_STUDY_PERMISSION } from '../../services/reviewWorkflowApi';

// -- PARENT COMPONENT: StudyList --
// Logged-in user id, used to scope review-workflow actions (submitter-only).
const getCurrentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.id ?? u?.user_id ?? u?.pk ?? null;
  } catch {
    return null;
  }
};

// Single status badge for study drafts. `review_status` is the source of truth;
// a draft with no review_status yet falls back to the `draft` entry. Completion
// (`status`: incomplete/complete) is shown as muted text, not a competing badge.
const REVIEW_BADGE = {
  draft: { label: 'Incomplete', cls: 'bg-amber-100 text-amber-800' },
  in_review: { label: 'Pending Review', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', cls: 'bg-teal-100 text-teal-800' },
  denied: { label: 'Incomplete', cls: 'bg-amber-100 text-amber-800' },
  published: { label: 'Published', cls: 'bg-emerald-100 text-emerald-800' },
};

function StudyList() {
  const navigate = useNavigate();
  const currentUserId = getCurrentUserId();
  const [canApproveStudy, setCanApproveStudy] = useState(false);
  const [studyDrafts, setStudyDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  // Draft whose full design is open in the detail modal. Stored by id and
  // resolved against the live list so the 30s silent refetch keeps the modal
  // header (e.g. review status) current instead of pinning a stale object.
  const [detailDraftId, setDetailDraftId] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'desc' });

  // silent=true refreshes the list in the background (no spinner, no error
  // toast) so polling/focus refetches don't flicker the table or interrupt.
  const loadStudyDrafts = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await studyBuilderService.getStudyDrafts();
      setStudyDrafts(response || []);
    } catch (error) {
      console.error('Error loading drafts:', error);
      if (!silent) toast.error('Failed to load drafts');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStudyDrafts();
  }, [loadStudyDrafts]);

  // Keep the list roughly live across users: silent refetch every 30s (matching
  // the notification bell's cadence) and whenever the tab regains focus, so a
  // submission/approval on another machine shows up without a manual refresh.
  useEffect(() => {
    const tick = () => loadStudyDrafts(true);
    const interval = setInterval(tick, 30000);
    const onFocus = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [loadStudyDrafts]);

  // Whether the current user can approve study drafts — enables inline approve/deny.
  useEffect(() => {
    let active = true;
    permissionsService
      .hasPermission(APPROVE_STUDY_PERMISSION)
      .then((ok) => { if (active) setCanApproveStudy(!!ok); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const handleEditClick = useCallback((draft) => {
    localStorage.removeItem('studyDrafts');
    localStorage.setItem('currentStudyId', String(draft.id));
    localStorage.setItem('currentStudyDraftId', String(draft.id));

    studyBuilderService.getStudyDrafts().then((resp) => {
      setStudyDrafts(resp);
    });

    navigate(`/studybuilder/${encodeURIComponent(draft.name)}`);
  }, [navigate]);

  // Open a submitted draft in review mode: same builder load as editing, but
  // lands on the review route which pins the Approve/Deny bar to the bottom.
  const handleReviewClick = useCallback((draft) => {
    localStorage.removeItem('studyDrafts');
    localStorage.setItem('currentStudyId', String(draft.id));
    localStorage.setItem('currentStudyDraftId', String(draft.id));
    navigate(`/studybuilder/review/${encodeURIComponent(draft.name)}`, {
      state: { reviewEntity: draft },
    });
  }, [navigate]);

  const formatDate = useMemo(() => (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  // Format status based on backend DRAFT_STATUS_CHOICES mapping
const getStatusDisplay = useMemo(() => (status) => {
    const statusMap = {
      'incomplete': 'Incomplete',
      'in_progress': 'In Progress',
      'ready_for_review': 'Ready for Review',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'archived': 'Archived'
    };
    return statusMap[status] || status;
  }, []);


  const handleSort = useCallback((key) => {
    setSortConfig((prevConfig) => {
      // If clicking the same column, toggle direction
      if (prevConfig.key === key) {
        return { 
          ...prevConfig, 
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc' 
        };
      }
      // If clicking a new column, default to ascending
      return { key, direction: 'asc' };
    });
  }, []);

  const sortedStudyDrafts = useMemo(() => {
    // Ensure studyDrafts is an array
    const draftsArray = Array.isArray(studyDrafts) ? studyDrafts : 
                       (studyDrafts && typeof studyDrafts === 'object' ? Object.values(studyDrafts) : []);
    
    console.log('Draft array after conversion:', draftsArray);
    const draftsToSort = [...draftsArray];
    console.log('Drafts to sort:', draftsToSort);
    if (sortConfig.key) {
      draftsToSort.sort((a, b) => {
        // First check if a or b are null/undefined
        if (!a) return 1;
        if (!b) return -1;
        
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle special cases (null, undefined, formatting)
        if (sortConfig.key === 'status') {
          aValue = getStatusDisplay(aValue) || '';
          bValue = getStatusDisplay(bValue) || '';
        }
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        // String comparison
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Numeric or date comparison
        try {
          return sortConfig.direction === 'asc' 
            ? (aValue > bValue ? 1 : -1) 
            : (bValue > aValue ? 1 : -1);
        } catch (error) {
          console.error('Error during sorting comparison:', error);
          return 0; // Return equal if comparison fails
        }
      });
    }
    return draftsToSort;
  }, [studyDrafts, sortConfig, getStatusDisplay]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/studybuilder')}
            className="mb-4 flex items-center text-primary-600 hover:text-primary-700 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            <span>Back to Study Builder</span>
          </button>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Study Drafts</h1>
        </motion.div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary-600 border-t-transparent"></div>
          </div>
        ) : studyDrafts.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900/20 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      <span>Study Name</span>
                      <div className="ml-1">
                        {sortConfig.key === 'name' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-primary-600" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-primary-600" />
                          )
                        ) : (
                          <div className="w-3 h-3 opacity-0 group-hover:opacity-40">
                            <ArrowUp className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      <span>Status</span>
                      <div className="ml-1">
                        {sortConfig.key === 'status' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-primary-600" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-primary-600" />
                          )
                        ) : (
                          <div className="w-3 h-3 opacity-0 group-hover:opacity-40">
                            <ArrowUp className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="flex items-center">
                      <span>Last Updated</span>
                      <div className="ml-1">
                        {sortConfig.key === 'updated_at' ? (
                          sortConfig.direction === 'asc' ? (
                            <ArrowUp className="w-3 h-3 text-primary-600" />
                          ) : (
                            <ArrowDown className="w-3 h-3 text-primary-600" />
                          )
                        ) : (
                          <div className="w-3 h-3 opacity-0 group-hover:opacity-40">
                            <ArrowUp className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                      </div>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {sortedStudyDrafts.length > 0 ? (
                  sortedStudyDrafts
                    .filter(draft => {
                      console.log('Checking draft for rendering:', draft);
                      return draft && typeof draft === 'object';
                    })
                    .map((draft) => (
                      <React.Fragment key={draft?.id || Math.random()}>
                        <tr className="group hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="p-2 rounded-full bg-gray-50 dark:bg-gray-900 group-hover:bg-white dark:group-hover:bg-gray-800 transition-colors duration-200">
                                <Book className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                              </div>
                              <div className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">{draft.name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              // Single status badge driven by review_status (defaults
                              // to Draft). Completion is muted text, not a second badge,
                              // and only matters while the draft is still editable.
                              const rs = draft.review_status || 'draft';
                              const badge = REVIEW_BADGE[rs] || REVIEW_BADGE.draft;
                              const showCompletion = rs === 'draft' || rs === 'denied';
                              const completion = draft.completion;
                              const isComplete = completion && completion.done >= completion.total;
                              return (
                                <div className="flex items-center gap-2">
                                  <span
                                    className={`px-3 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${badge.cls}`}
                                  >
                                    {badge.label}
                                  </span>
                                  {showCompletion && completion && (
                                    <span
                                      className={`text-xs ${isComplete ? 'text-gray-500 dark:text-gray-400' : 'text-amber-600 dark:text-amber-400'}`}
                                    >
                                      {completion.done}/{completion.total} complete
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                              <Clock className="w-4 h-4 mr-1.5" />
                              {formatDate(draft.updated_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end space-x-4">
                              <ReviewWorkflowActions
                                entity={draft}
                                entityType="study"
                                currentUserId={currentUserId}
                                isApprover={canApproveStudy}
                                onChanged={loadStudyDrafts}
                                onReview={handleReviewClick}
                              />
                              {['in_review', 'approved', 'published'].includes(draft.review_status) ? (
                                <span
                                  className="flex items-center text-gray-300 dark:text-gray-600 cursor-not-allowed"
                                  title="Locked. Revoke the approval or recall the study to edit it."
                                >
                                  <div className="p-1.5 rounded-full">
                                    <Lock className="w-4 h-4" />
                                  </div>
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleEditClick(draft)}
                                  className="group/edit flex items-center text-gray-400 dark:text-gray-500 hover:text-primary-600 transition-colors duration-200"
                                  title="Edit Draft"
                                >
                                  <div className="p-1.5 rounded-full group-hover/edit:bg-primary-50 transition-colors duration-200">
                                    <Edit className="w-4 h-4" />
                                  </div>
                                </button>
                              )}
                              <button
                                onClick={() => setDetailDraftId(draft.id)}
                                className="group/view flex items-center text-gray-400 dark:text-gray-500 hover:text-primary-600 transition-colors duration-200"
                                title="View design details"
                              >
                                <div className="p-1.5 rounded-full group-hover/view:bg-primary-50 transition-colors duration-200">
                                  <Eye className="w-4 h-4" />
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20">
                      <div className="p-3 rounded-full bg-gray-50 dark:bg-gray-900 w-fit mx-auto mb-4">
                        <Book className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No study drafts found</h3>
                      <p className="text-gray-500 dark:text-gray-400">Create your first study to get started</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/20">
            <div className="p-3 rounded-full bg-gray-50 dark:bg-gray-900 w-fit mx-auto mb-4">
              <Book className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No study drafts found</h3>
            <p className="text-gray-500 dark:text-gray-400">Create your first study to get started</p>
          </div>
        )}
      </div>

      <StudyDraftDetailModal
        open={detailDraftId !== null}
        draft={studyDrafts.find((d) => d?.id === detailDraftId) || null}
        onClose={() => setDetailDraftId(null)}
      />
    </div>
  );
}

export default StudyList;